import { Grid, Group, Layout, Page } from "@/comn/components";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useToast } from "@/comn/hooks";
import {
    APIS,
    PGEUIDS,
    SF_ROLE_MGMT_DTL,
    SG_ROLE_LANG_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/role/services/OsciRoleService";
import React, { useEffect } from "react";
import { SG_ROLE_AUTH_CNCT_LIST } from "@/tz/go/egaz/osci/ptl/ptl/auth/services/OsciRoleAuthService";
import { OSCI0104018Q } from "@/tz/go/egaz/osci/ptl/ptl/role/OSCI0104018Q";
import { OSCI0104019Q } from "@/tz/go/egaz/osci/ptl/ptl/role/OSCI0104019Q";

/*
 * Modifying and Deleting role management
 * !== 역할 관리 수정 및 삭제 ==!
 */
export const OSCI0104017S = (): JSX.Element => {
    const { pgeUid } = usePage({
        auth: ["auth_osci0003"],
    });
    const navigate = useNavigate();
    const modal = useModal();
    const toast = useToast();
    const { roleId } = useParams();
    const langCodes = ["KO", "EN", "TZ"];
    const { setStore, getStore } = useStore({ pgeUid: pgeUid });
    const { t } = useTranslation();

    useResource({ defaultSchema: [{ area: "comnCd", comnCd: "OSC0095" }] });

    const form = {
        roleDtl: useForm({ defaultSchema: SF_ROLE_MGMT_DTL }),
    };

    const grid = {
        roleLangList: useGrid({ defaultSchema: SG_ROLE_LANG_LIST }),
        roleAuthList: useGrid({ defaultSchema: SG_ROLE_AUTH_CNCT_LIST }),
    };

    const fetch = {
        getRoleDtl: useFetch({
            api: (data: any) => APIS.getRoleDtl(data),
            onSuccess: (data: any) => {
                const { roleDtl } = data;

                if (!roleDtl.content) {
                    modal.openModal({ content: "msg.osc.00050" }); // No data found. !== 해당 데이터가 없습니다. ==!
                    navigate(URLS.osci0104015q);
                    return;
                }

                const { roleLangList, cnctList, ...rest } = roleDtl.content;
                form.roleDtl.setValues(rest);

                if (!comnUtils.isEmpty(roleLangList)) {
                    roleLangList.forEach((item: any) => {
                        const { langCd, roleNm, roleDesc } = item;
                        form.roleDtl.setValue(`roleNm${langCd}`, roleNm);
                        form.roleDtl.setValue(`roleDesc${langCd}`, roleDesc);
                    });
                }

                grid.roleAuthList.setData({ content: cnctList });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00035" }), // An error occurred. !== 에러가 발생했습니다! ==!
        }),
        updateRoleDtl: useFetch({
            api: () =>
                APIS.updateRoleDtl({
                    ...form.roleDtl.getValues(),
                    roleLangList: grid.roleLangList.getData(),
                    cnctList: grid.roleAuthList.getData({ excludes: ["origin"] }),
                }),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00069",
                    onClose: () => handler.onListBtnClick(),
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00045" }), // 저장이 실패하였습니다.
        }),
        deleteRoleDtl: useFetch({
            api: () => APIS.deleteRoleDtl(form.roleDtl.getValues()),
            onSuccess: () => {
                modal.openModal({ content: "msg.osc.00071" }); // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                handler.onListBtnClick();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00040" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),

        // Select Role user mapping !== 역할 사용자 매핑 정보 조회 ==!
        getRoleMapInfo: useFetch({
            api: (data: any) => APIS.getRoleMapInfo(data),
            onSuccess: (data: any) => {
                const roleMapInfo = data.roleMapInfo.content;
                if (roleMapInfo.roleAuthMapYn === "Y") {
                    modal.openModal({ content: t("msg.osc.00257", { 0: t("L_AUTH") }) }); // Deletion is impossible if there is at least one (0) mapped. !== {0}가 하나라도 매핑되어 있는 경우 삭제가 불가능합니다. ==!
                    return;
                }

                if (roleMapInfo.usrRoleMapYn === "Y") {
                    modal.openModal({ content: t("msg.osc.00261", { 0: t("L_ROLE_ID") }) }); // Deletion is not possible if at least one user with that {0} is mapped. !== 해당 {0}을 가진 사용자가 한명이라도 매핑되어 있는 경우 삭제가 불가능합니다. ==!
                    return;
                }

                fetch.deleteRoleDtl.fetch();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00040" }), // Failed to remove. !== 삭제가 실패하였습니다. ==!
        }),
    };

    const handler = {
        onGridAddBtnClick: () => {
            const modalCallback = (list: any[]) => {
                if (!comnUtils.isEmpty(list)) {
                    list.forEach((item: any) => grid.roleAuthList.addRow({ ...item, roleId: roleId }));
                }
                modal.closeModal();
            };

            modal.openModal({
                draggable: true,
                size: "xl",
                content: (
                    <OSCI0104018Q
                        roleId={roleId}
                        ptlTpCd={form.roleDtl.getValue("ptlTpCd")}
                        callback={modalCallback}
                        roleAuthList={grid.roleAuthList.getData()}
                    />
                ),
            });
        },
        onGridDelBtnClick: () => {
            if (!grid.roleAuthList.isChecked()) {
                modal.openModal({ content: "msg.osc.00193" }); // Please select a destination to delete. !== 삭제대상을 선택하세요. ==!
                return;
            }
            modal.openModal({
                content: "msg.osc.00062",
                onConfirm: () => {
                    grid.roleAuthList.deleteRow("checkbox");
                },
            });
        },
        onListBtnClick: () => navigate(`${URLS.osci0104015q}`),
        onSaveBtnClick: form.roleDtl.handleSubmit(
            () => {
                modal.openModal({
                    content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                    onConfirm: () => {
                        grid.roleLangList.clearData();

                        langCodes.forEach((langCd: any) => {
                            const roleNm = form.roleDtl.getValue(`roleNm${langCd}`);
                            const roleDesc = form.roleDtl.getValue(`roleDesc${langCd}`);
                            grid.roleLangList.addRow({
                                langCd: langCd,
                                roleId: roleId,
                                roleNm: roleNm,
                                roleDesc: roleDesc,
                            });
                        });

                        fetch.updateRoleDtl.fetch();
                    },
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.osc.00062", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => fetch.getRoleMapInfo.fetch(form.roleDtl.getValue("roleId")),
            });
        },
    };

    const render = {
        roleAuthList: {
            cell: {
                authRelaMenuCnt: (args: any) => {
                    const { rowValues, value } = args;
                    return (
                        <button
                            className="underline"
                            onClick={() => {
                                modal.openModal({
                                    draggable: true,
                                    size: "sm",
                                    content: (
                                        <OSCI0104019Q
                                            ptlTpCd={form.roleDtl.getValues("ptlTpCd")}
                                            authId={rowValues.authId}
                                        />
                                    ),
                                });
                            }}
                        >
                            {value}
                        </button>
                    );
                },
            },
        },
    };

    useEffect(() => {
        const mainStore = getStore(PGEUIDS.osci0104015q);
        if (comnUtils.isEmpty(mainStore) || comnUtils.isEmpty(mainStore.param)) {
            modal.openModal({ content: "msg.osc.00201" }); // Abnormal access. !== 비정상 접근입니다. ==!
            handler.onListBtnClick();
            return;
        }
        form.roleDtl.setEditable("roleId", false);
        form.roleDtl.setEditable("ptlTpCd", false);
        console.log(mainStore.param);
        fetch.getRoleDtl.fetch(mainStore.param);

        return () => {
            const { param, ...rest } = mainStore;
            setStore(PGEUIDS.osci0104015q, rest);
        };
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleId} />
                            <Group.Control {...form.roleDtl.schema.useYn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmTZ} />
                            <Group.Control {...form.roleDtl.schema.roleDescTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmEN} />
                            <Group.Control {...form.roleDtl.schema.roleDescEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.roleDtl.schema.roleNmKO} />
                            <Group.Control {...form.roleDtl.schema.roleDescKO} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section hidden={true}>
                        <Grid {...grid.roleLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Layout>
                            <Layout.Right>
                                <Layout.Button role={"gridAdd"} onClick={handler.onGridAddBtnClick} />
                                <Layout.Button role={"gridDelete"} onClick={handler.onGridDelBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Section>
                    <Group.Section>
                        <Grid {...grid.roleAuthList.grid} render={render.roleAuthList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={() => handler.onListBtnClick()} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onSaveBtnClick} />
                    {/*<Layout.Button role={"delete"} onClick={handler.onDeleteBtnClick} /> */}
                </Layout.Right>
            </Layout>
        </Page>
    );
};
